/* global HSCore */
/* global bootstrap */

import React, { useContext, useState, useEffect } from "react";
import LanguageContext from "../../../utils/Contexts/LanguageContext";
import UserContext from "../../../utils/Contexts/UserContext";
import axiosInstance from "../../../utils/Requests/axios";
import toast from "react-hot-toast";
import _ from "lodash";
import ThemeContext from "../../../utils/Contexts/ThemeContext";

function OffersSection({ termsData, id, setTermsData }) {
  const { userData } = useContext(UserContext);
  const { t } = useContext(LanguageContext);
  const { selectedTheme } = useContext(ThemeContext);
  const [showAddOffer, setShowAddOffer] = useState(false);
  const [newOffer, setNewOffer] = useState({
    type: null,
    rel: null,
    niche: null,
    links: null,
    cost: null,
    offer: null,
  });
  const [editIndex, setEditIndex] = useState(null);
  const [editedValues, setEditedValues] = useState({
    cost: null,
    offer: null,
    uuid: "",
  });

  const handleEditClick = (index, offer) => {
    setEditIndex(index);
    setEditedValues({ cost: offer.cost, offer: offer.offer, uuid: offer.uuid });
    setShowAddOffer(false);
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setNewOffer((prevState) => ({
      ...prevState,
      [name]:
        (name === "cost" || name === "offer") && value ? Number(value) : value,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedValues((prevState) => ({
      ...prevState,
      [name]: value ? Number(value) : value,
    }));
  };

  const handleAddOfferClick = () => {
    setShowAddOffer(!showAddOffer);
    setEditIndex(null);
  };

  const handleAddNewOffer = async () => {
    if (editIndex === null) {
      const { type, rel, niche, links, cost, offer } = newOffer;
      if (
        !type ||
        !rel ||
        !niche ||
        !links ||
        !(cost !== "" && Number(cost) >= 0) ||
        !offer
      ) {
        toast.error("Please fill all offer fields.");
        return;
      }
    }
    if (editIndex !== null) {
      const { cost, offer } = editedValues;
      if (!(cost !== "" && Number(cost) >= 0) || !offer) {
        toast.error("Please add offer cost and price.");
        return;
      }
    }
    const url = `/sellers/${userData?.uuid}/offers`;
    const payload =
      editIndex != null ? editedValues : { ...newOffer, domain_uuid: id };
    try {
      const response = await axiosInstance.put(url, payload);
      const clonedData = _.cloneDeep(termsData);
      const responseData = response?.data;
      if (editIndex === null) {
        clonedData.offers.push(responseData);
        setTermsData(clonedData);
        setShowAddOffer(false);
        setNewOffer({
          type: null,
          rel: null,
          niche: null,
          links: null,
          cost: null,
          offer: null,
        });
        toast.success(t("Offer added successfully"));
      } else {
        const offerIndex = clonedData.offers.findIndex(
          (offer) => offer.uuid === responseData.uuid
        );
        if (offerIndex !== -1) {
          clonedData.offers[offerIndex].cost = responseData.cost;
          clonedData.offers[offerIndex].price = responseData.price;
        }
        setTermsData(clonedData);
        setEditIndex(null);
        toast.success(t("Offer updated successfully"));
      }
    } catch (error) {
      toast.error(t("Error on saving offer"));
    }
  };

  const cancelOffer = () => {
    setShowAddOffer(false);
    setEditIndex(null);
  };

  useEffect(() => {
    if (showAddOffer) {
      HSCore.components.HSTomSelect.init(".js-select-offer");
    }
  }, [showAddOffer]);

  const handleDeleteOffer = (uuid) => {
    const url = `/sellers/${userData?.uuid}/offers/${uuid}`;
    axiosInstance
      .delete(url)
      .then(() => {
        toast.success(t("Offer deleted successfully"));
        const clonedData = _.cloneDeep(termsData);
        clonedData.offers = clonedData.offers.filter(
          (offer) => offer.uuid !== uuid
        );
        setTermsData(clonedData);
      })
      .catch(() => {
        toast.error(t("There was an error deleting the offer"));
      });
  };

  const tomSelectOptionsType = {
    placeholder: t("Select Type"), // Localized text
    hideSearch: true,
    dropdownParent: "body",
  };
  const tomSelectOptionsRel = {
    placeholder: t("Select Rel"), // Localized text
    hideSearch: true,
    dropdownParent: "body",
  };
  const tomSelectOptionsNiche = {
    placeholder: t("Select Niche"), // Localized text
    hideSearch: true,
    dropdownParent: "body",
  };
  const tomSelectOptionsLinks = {
    placeholder: t("Select Links"), // Localized text
    hideSearch: true,
    dropdownParent: "body",
  };

  let tooltipInit = false;
  useEffect(() => {
    if (!tooltipInit) {
      const tooltipTriggerList = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]'
      );
      [...tooltipTriggerList].forEach(
        (tooltipTriggerEl) => new bootstrap.Tooltip(tooltipTriggerEl)
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
      tooltipInit = true;
    }
  }, []);

  return (
    <div
      id="offersSection"
      className={`card ${showAddOffer ? "pb-2" : "pb-3"} card-setting`}
    >
      <div className="card-header">
        <h4 className="card-title">{t("Offer(s)")}</h4>
      </div>

      <div className="table-responsive datatable-custom">
        <table
          className="table table-thead-bordered table-nowrap card-table"
          style={{ border: "transparent" }}
        >
          <thead className="thead-light">
            <tr>
              <th>{t("TYPE")}</th>
              <th>{t("REL TYPE")}</th>
              <th>{t("NICHE")}</th>
              <th>{t("LINKS")}</th>
              <th>
                {t("COST")}
                <i
                  className="bi-question-circle text-secondary ms-1"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Offer Cost"
                />
              </th>
              <th>
                {t("PRICE")}
                <i
                  className="bi-question-circle text-secondary ms-1"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Offer Price"
                />
              </th>
              <th>{t("FEE")}</th>
              <th>{t("SELLING PRICE")}</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {termsData?.offers?.map((offer, index) => (
              <React.Fragment key={index}>
                <tr className="align-middle tbody-light">
                  <td className="text-dark ps-4">
                    <span style={{ display: "block", width: "180px" }}>
                      {offer?.type === 1
                        ? t("Guest Post")
                        : offer?.type === 2
                        ? t("Link Insert")
                        : offer?.type === 3
                        ? t("Niche Edit")
                        : ""}
                    </span>
                  </td>
                  <td
                    className={`${
                      offer.rel === 1 ? "text-primary" : "text-dark"
                    }`}
                  >
                    <span style={{ display: "block", width: "160px" }}>
                      {offer.rel === 1
                        ? t("Follow")
                        : offer.rel === 2
                        ? t("No Follow")
                        : offer.rel === 3
                        ? t("Sponsored")
                        : ""}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "block", width: "250px" }}>
                      <div
                        className={`py-1 rounded text-center ${
                          offer.niche === 1
                            ? "bg-soft-primary text-primary"
                            : offer.niche === 2 ||
                              offer.niche === 4 ||
                              offer.niche === 6
                            ? "bg-soft-secondary text-secondary"
                            : "bg-soft-secondary text-dark"
                        }`}
                        style={{ width: "98px", height: "29px" }}
                      >
                        {offer?.niche === 1
                          ? t("General")
                          : offer?.niche === 2
                          ? t("Gambling")
                          : offer?.niche === 3
                          ? t("Crypto")
                          : offer?.niche === 4
                          ? t("Medications")
                          : offer?.niche === 5
                          ? t("Dating")
                          : offer?.niche === 6
                          ? t("Adult")
                          : ""}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span style={{ display: "block", width: "120px" }}>
                      {offer?.links}
                    </span>
                  </td>
                  <td>
                    {editIndex === index ? (
                      <input
                        type="number"
                        className="form-control form-control-sm input-terms"
                        style={{ minHeight: "unset" }}
                        name="cost"
                        value={editedValues.cost}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <div style={{ width: "90px" }}>{offer?.cost}</div>
                    )}
                  </td>
                  <td>
                    {editIndex === index ? (
                      <input
                        type="number"
                        className="form-control form-control-sm input-terms"
                        style={{ minHeight: "unset" }}
                        name="offer"
                        value={editedValues.offer}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <div style={{ width: "90px" }}>{offer?.offer}</div>
                    )}
                  </td>
                  <td>{offer?.price - offer?.offer}</td>
                  <td>{offer?.price}</td>
                  <td className="px-0">
                    <div className="dropdowm">
                      <button
                        type="button"
                        className="btn btn-ghost-secondary btn-icon btn-sm rounded-circle"
                        id="teamsDropdown2"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <i className="bi-three-dots-vertical"></i>
                      </button>

                      <div
                        className="dropdown-menu dropdown-menu-sm dropdown-menu-end"
                        aria-labelledby="teamsDropdown2"
                      >
                        <button
                          className="dropdown-item"
                          onClick={() => handleEditClick(index, offer)}
                        >
                          <i className="bi-pencil-fill dropdown-item-icon"></i>
                          {t("Edit")}
                        </button>
                        <div className="dropdown-divider"></div>
                        <button
                          className="dropdown-item text-danger"
                          onClick={() => handleDeleteOffer(offer?.uuid)}
                        >
                          <i className="bi-x-circle dropdown-item-icon"></i>{" "}
                          {t("Delete")}
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                {(index !== termsData?.offers?.length - 1 || showAddOffer) && (
                  <tr>
                    <td
                      colSpan="100%"
                      className="px-3 py-0"
                      style={{ border: "transparent" }}
                    >
                      <div
                        style={{
                          width: "calc(100% - 2px)",
                          height: "2px",
                          backgroundColor:
                            selectedTheme === "dark"
                              ? "rgba(45, 50, 54, 1)"
                              : "rgba(45, 50, 54, 0.2)",
                          margin: "0 auto",
                        }}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}

            {showAddOffer && (
              <tr>
                <td className="ps-3">
                  <div className="tom-select-custom">
                    <select
                      className="js-select-offer form-select js-type"
                      autoComplete="off"
                      data-hs-tom-select-options={JSON.stringify(
                        tomSelectOptionsType
                      )}
                      name="type"
                      value={newOffer.type}
                      onChange={handleSelectChange}
                    >
                      <option value="">{t("Select Type")}</option>
                      <option value={1}>{t("Guest Post")}</option>
                      <option value={2}>{t("Link Insert")}</option>
                      <option value={3}>{t("Niche Edit")}</option>
                    </select>
                  </div>
                </td>
                <td className="ps-2">
                  <div className="tom-select-custom">
                    <select
                      className="js-select-offer form-select js-rel"
                      autoComplete="off"
                      name="rel"
                      data-hs-tom-select-options={JSON.stringify(
                        tomSelectOptionsRel
                      )}
                      value={newOffer.rel}
                      onChange={handleSelectChange}
                    >
                      <option value="">{t("Select Rel")}</option>
                      <option value={1}>{t("Follow")}</option>
                      <option value={2}>{t("No Follow")}</option>
                      <option value={3}>{t("Sponsored")}</option>
                    </select>
                  </div>
                </td>
                <td className="ps-2">
                  <div className="tom-select-custom">
                    <select
                      className="js-select-offer form-select js-niche"
                      autoComplete="off"
                      name="niche"
                      data-hs-tom-select-options={JSON.stringify(
                        tomSelectOptionsNiche
                      )}
                      value={newOffer.niche}
                      onChange={handleSelectChange}
                    >
                      <option value="">{t("Select Niche")}</option>
                      <option value={1}>{t("General")}</option>
                      <option value={2}>{t("Gambling")}</option>
                      <option value={3}>{t("Crypto")}</option>
                      <option value={4}>{t("Medications")}</option>
                      <option value={5}>{t("Dating")}</option>
                      <option value={6}>{t("Adult")}</option>
                    </select>
                  </div>
                </td>
                <td className="ps-2">
                  <div className="tom-select-custom">
                    <select
                      className="js-select-offer form-select js-links"
                      autoComplete="off"
                      data-hs-tom-select-options={JSON.stringify(
                        tomSelectOptionsLinks
                      )}
                      name="links"
                      value={newOffer.links}
                      onChange={handleSelectChange}
                    >
                      <option value="">{t("Select Links")}</option>
                      <option value={1}>1</option>
                      <option value={2}>2</option>
                      <option value={3}>3</option>
                    </select>
                  </div>
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control form-control-sm input-terms"
                    name="cost"
                    value={newOffer.cost}
                    style={{ minHeight: "unset" }}
                    onChange={handleSelectChange}
                    placeholder={t("Enter cost")}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    name="offer"
                    className="form-control form-control-sm input-terms py-0"
                    style={{ minHeight: "unset" }}
                    value={newOffer.offer}
                    onChange={handleSelectChange}
                    placeholder={t("Enter price")}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!showAddOffer && (
        <div
          className={`mt-2 ${
            !showAddOffer && editIndex === null && "mb-2"
          } mb-1 px-3 text-primary`}
        >
          <span style={{ cursor: "pointer" }} onClick={handleAddOfferClick}>
            + {t("Add Offer")}
          </span>
        </div>
      )}
      {(editIndex !== null || showAddOffer !== false) && (
        <>
          <hr style={{ width: "100%", margin: "4px 0px" }} />
          <div className="d-flex justify-content-end gap-3 px-4 py-3">
            <button
              className="btn btn-outline-primary"
              onClick={cancelOffer}
              style={{ padding: "0.38rem 1.83rem" }}
            >
              {t("Cancel")}
            </button>

            <button
              className="btn btn-primary"
              onClick={handleAddNewOffer}
              style={{ padding: "0.38rem 2.23rem" }}
            >
              {t("Save")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default OffersSection;
